import { RuleId } from '../rules';
import { AttributionSummary, FactClass } from './attribute';
import { PlayerRecord, emptyRecord } from './record';
import { ALL_RULE_IDS, RULE_NAMES } from './rulemeta';

// Rolling per-replay attributions up into a player profile.
//
// Everything here is arithmetic over `AttributionSummary` — no re-analysis — so a profile is
// cheap to recompute and the expensive part (attribution) can be cached per replay.
//
// The three signals this exposes are the ones the corpus already validates and which need no
// timing model to be meaningful: mistake rate (0.2%–7.1% across real players), guess rate (some
// players never guess, others guess on half their placements), and lookahead depth (how many
// rounds of elimination a player carries in their head before committing). Per-technique search
// times are the fourth, and the most speculative — they are medians over unambiguously
// attributed single-deduction bursts, not fitted parameters.
//
// ── On reading these numbers as a verdict ──
//
// A guess rate is a *style*, not a grade. Laying queens down speculatively and re-fitting them is
// a way of solving Queens with its own ladder of skill — how far you narrow a unit before you
// commit, how often the commit lands against what the geometry alone would give you, how much
// work you build on top before you notice, and whether moving the queen costs you the board. The
// derived profile below measures that ladder (`ExplorationProfile`) so an explorer is described
// by how well they explore rather than by how much they deviate from a deducer. `highlightsFor`
// therefore leads with strengths, always names at least one, and emits at most a single "next
// lever" — which is chosen for expected payoff, not to complete a report card.

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

/**
 * Real players are split across several cookie tokens — one per device, plus re-namings. Left
 * unmerged, the strongest players fragment into underpowered shards. Names are matched
 * case-insensitively; the device suffixes below were confirmed by hand against the corpus.
 */
const ALIASES: Record<string, string> = {
  'giby': 'Giby',
  'gibydesktop': 'Giby',
  "giby's phone": 'Giby',
  'giby desktop': 'Giby',
  'stephanie': 'Stephanie',
  'steph': 'Stephanie',
  'steph browser': 'Stephanie',
  'tek': 'Tek',
  'tel': 'Tek',
};

/** The key two raw player names must share to be treated as the same person. */
export function canonicalPlayerKey(rawName: string): string {
  const k = rawName.trim().toLowerCase();
  return (ALIASES[k] ?? k).toLowerCase();
}

/** The name to show for a merged group, when the alias table settles it. */
export function canonicalDisplayName(rawName: string): string | null {
  return ALIASES[rawName.trim().toLowerCase()] ?? null;
}

// ---------------------------------------------------------------------------
// Accumulation
// ---------------------------------------------------------------------------

/** One analyzed completion, as stored in the attribution cache. */
export interface SolveRecord {
  leaderboardId: number;
  puzzleId: number;
  puzzleName: string;
  playerName: string;
  playerToken: string;
  size: number;
  timeMs: number;
  completedAt: number;
  summary: AttributionSummary;
}

export interface PlayerStats {
  key: string;
  displayName: string;
  tokens: string[];
  solves: number;
  totalTimeMs: number;
  facts: number;
  xMarks: number;
  queenMarks: number;
  autoXMarks: number;
  byClass: Record<FactClass, number>;
  queens: number;
  queensForced: number;
  queensLookahead: number;
  queensCommit: number;
  queensWrong: number;
  commitWidths: Record<number, number>;
  aheadHits: Record<number, number>;
  aheadMisses: Record<number, number>;
  recoveryMarks: number[];
  recoveryByClear: number;
  unrecovered: number;
  bursts: number;
  undos: number;
  ruleCounts: Record<string, number>;
  ambiguity: Record<number, number>;
  lookaheadDepth: Record<number, number>;
  ruleGaps: Record<string, number[]>;
  searchGaps: number[];
  motor: { n: number; sx: number; sy: number; sxx: number; sxy: number };
  /** size → solve times, for the size-matched speed comparison. */
  timesBySize: Record<number, number[]>;
  autoXsSolves: number;
}

function emptyStats(key: string, displayName: string): PlayerStats {
  return {
    key, displayName, tokens: [], solves: 0, totalTimeMs: 0, facts: 0,
    xMarks: 0, queenMarks: 0, autoXMarks: 0,
    byClass: {
      bookkeeping: 0, inference: 0, lookahead: 0, 'changed-mind': 0,
      mistake: 0, fallout: 0, commit: 0, unexplained: 0, broken: 0,
    },
    queens: 0, queensForced: 0, queensLookahead: 0, queensCommit: 0, queensWrong: 0,
    commitWidths: {}, aheadHits: {}, aheadMisses: {},
    recoveryMarks: [], recoveryByClear: 0, unrecovered: 0,
    bursts: 0, undos: 0, ruleCounts: {}, ambiguity: {}, lookaheadDepth: {},
    ruleGaps: {}, searchGaps: [], motor: { n: 0, sx: 0, sy: 0, sxx: 0, sxy: 0 },
    timesBySize: {}, autoXsSolves: 0,
  };
}

function addInto(acc: PlayerStats, rec: SolveRecord): void {
  const s = rec.summary;
  acc.solves++;
  acc.totalTimeMs += rec.timeMs;
  acc.facts += s.facts;
  acc.xMarks += s.xMarks;
  acc.queenMarks += s.queenMarks;
  acc.autoXMarks += s.autoXMarks;
  for (const k of Object.keys(acc.byClass) as FactClass[]) acc.byClass[k] += s.byClass[k] ?? 0;
  acc.queens += s.queens;
  acc.queensForced += s.queensForced;
  acc.queensLookahead += s.queensLookahead;
  acc.queensCommit += s.queensCommit;
  acc.queensWrong += s.queensWrong;
  acc.bursts += s.bursts;
  acc.undos += s.undos;
  if (s.mode === 'autoXs') acc.autoXsSolves++;
  if (!acc.tokens.includes(rec.playerToken)) acc.tokens.push(rec.playerToken);

  for (const [w, n] of Object.entries(s.commitWidths)) {
    acc.commitWidths[Number(w)] = (acc.commitWidths[Number(w)] ?? 0) + n;
  }
  for (const [w, n] of Object.entries(s.aheadHits)) {
    acc.aheadHits[Number(w)] = (acc.aheadHits[Number(w)] ?? 0) + n;
  }
  for (const [w, n] of Object.entries(s.aheadMisses)) {
    acc.aheadMisses[Number(w)] = (acc.aheadMisses[Number(w)] ?? 0) + n;
  }
  acc.recoveryMarks.push(...s.recoveryMarks);
  acc.recoveryByClear += s.recoveryByClear;
  acc.unrecovered += s.unrecovered;
  for (const [r, n] of Object.entries(s.ruleCounts)) acc.ruleCounts[r] = (acc.ruleCounts[r] ?? 0) + n;
  for (const [a, n] of Object.entries(s.ambiguity)) acc.ambiguity[Number(a)] = (acc.ambiguity[Number(a)] ?? 0) + n;
  for (const [d, n] of Object.entries(s.lookaheadDepth)) acc.lookaheadDepth[Number(d)] = (acc.lookaheadDepth[Number(d)] ?? 0) + n;
  for (const [r, t] of Object.entries(s.ruleTime)) {
    // Mean-per-replay rather than every raw gap: the cached summary already aggregates, and a
    // replay's worth of gaps for one rule is one observation of "how long this player takes".
    const arr = acc.ruleGaps[r] ?? (acc.ruleGaps[r] = []);
    if (t.n > 0) arr.push(t.totalMs / t.n);
  }
  acc.searchGaps.push(...s.searchGaps);
  acc.motor.n += s.motor.n;
  acc.motor.sx += s.motor.sx;
  acc.motor.sy += s.motor.sy;
  acc.motor.sxx += s.motor.sxx;
  acc.motor.sxy += s.motor.sxy;
  (acc.timesBySize[rec.size] ?? (acc.timesBySize[rec.size] = [])).push(rec.timeMs);
}

/** Group solves by merged identity. `population` accumulates every solve regardless of player. */
export function aggregate(records: SolveRecord[]): { players: PlayerStats[]; population: PlayerStats } {
  const byKey = new Map<string, PlayerStats>();
  const nameVotes = new Map<string, Map<string, number>>();
  const population = emptyStats('__all__', 'Everyone');

  for (const rec of records) {
    const key = canonicalPlayerKey(rec.playerName);
    const acc = byKey.get(key) ?? byKey.set(key, emptyStats(key, rec.playerName)).get(key)!;
    addInto(acc, rec);
    addInto(population, rec);
    const votes = nameVotes.get(key) ?? nameVotes.set(key, new Map()).get(key)!;
    votes.set(rec.playerName, (votes.get(rec.playerName) ?? 0) + 1);
  }

  // Display under the alias table's name where it has one ("Giby", not "Giby's Phone"), and
  // otherwise under the spelling the player used most.
  for (const [key, votes] of nameVotes) {
    const entries = [...votes.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    const named = entries.map(e => canonicalDisplayName(e[0])).find(n => n !== null);
    byKey.get(key)!.displayName = named ?? entries[0][0];
  }

  return {
    players: [...byKey.values()].sort((a, b) => b.solves - a.solves),
    population,
  };
}

// ---------------------------------------------------------------------------
// Derived profile
// ---------------------------------------------------------------------------

export function median(xs: number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export interface RuleUsage {
  rule: RuleId;
  name: string;
  count: number;
  /** Share of this player's explained facts. */
  share: number;
  /** Same share across everyone — the reference line. */
  popShare: number;
  /** Median search time before an unambiguous single-deduction burst using this rule. */
  medianMs: number | null;
  popMedianMs: number | null;
  /** medianMs / popMedianMs. <1 means faster than average at this technique. */
  speedRatio: number | null;
  samples: number;
}

export interface SizeSpeed {
  size: number;
  solves: number;
  medianMs: number;
  popMedianMs: number | null;
}

export interface Highlight {
  /** `focus` is the one thing worth working on next — at most one is ever emitted. */
  kind: 'strength' | 'focus' | 'note';
  label: string;
  text: string;
}

/** How the player gets to a solution. Every style is a way of playing well, not a score. */
export interface PlayStyle {
  key: 'explorer' | 'deducer' | 'visualizer' | 'archivist' | 'allrounder';
  label: string;
  text: string;
}

/**
 * The skill inside speculative play: how well a player picks a square the board has not proved,
 * and what it costs them when the pick is wrong. See the note on the summary fields in
 * `attribute.ts` for what does and does not get scored, and why.
 */
export interface ExplorationProfile {
  /** Queen placements the visible board had not forced, scored on a clean board. */
  ahead: number;
  hits: number;
  /** Share of them that turned out to fit. */
  hitRate: number | null;
  /** What picking uniformly among the candidates left in the same units would have scored —
   *  the honest baseline a hit rate has to beat before it means anything. */
  chanceRate: number | null;
  /** hitRate / chanceRate. Above 1 ⇒ the choice of square is doing work, not just the geometry. */
  lift: number | null;
  /** Mean candidates left in the tightest unit at the moment of the pick. */
  meanWidth: number | null;
  /** Share of picks made into a unit with exactly two candidates — the cheapest place to be
   *  wrong, and the mark of narrowing before choosing rather than choosing to narrow. */
  narrowShare: number | null;
  /** Per-width breakdown, so the view can draw each hit rate against its own 1/width baseline. */
  byWidth: Array<{ width: number; hits: number; total: number }>;
  /** Queens that did not fit and were taken back off. */
  recoveries: number;
  /** Median marks made on top of one before it moved — the cost of a wrong branch. */
  medianRecoveryMarks: number | null;
  /** Share of recoveries that wiped the board rather than moving the queen. */
  clearShare: number | null;
}

export interface PlayerProfile {
  key: string;
  displayName: string;
  tokens: number;
  solves: number;
  facts: number;
  /** Marks split by what was written — the raw volume behind everything else on the page. */
  xMarks: number;
  queenMarks: number;
  /** Eliminations the Auto Xs overlay made on the player's behalf. Not part of `facts`, and not
   *  part of `xMarks` either — the two are added together where the page shows total volume. */
  autoXMarks: number;
  /** Xs per queen, counting the ones Auto Xs placed. High means a board written out in full; low
   *  means one carried in the head. */
  xPerQueen: number | null;
  /** Solves, wins and time on the clock, from the leaderboard rather than from replays. */
  record: PlayerRecord;

  /** Move mix, as raw counts — the client renders the stacked bar itself. */
  byClass: Record<FactClass, number>;
  /** Marks that contradict the solution with nothing already wrong on the board: real slips. */
  mistakeRate: number;
  /** Marks that only contradict the solution because a speculative queen upstream did. One
   *  decision's consequences, not N decisions — kept separate so exploring is not billed twice. */
  falloutRate: number;
  bookkeepingShare: number;
  unexplainedRate: number;

  /** Queen decisions, and how the player arrived at each one. Sums to `queens`. */
  queens: number;
  queenSplit: { forced: number; lookahead: number; commit: number; wrong: number; changedMind: number };
  guessRate: number;
  lookaheadShare: number;
  meanLookaheadDepth: number;
  lookaheadDepth: Record<number, number>;
  commitWidths: Record<number, number>;

  /** Attribution quality — how often the rule set pins a fact to exactly one technique. */
  uniqueAttributionRate: number;

  undosPerSolve: number;
  medianSolveMs: number | null;
  medianSearchMs: number | null;
  /** Least-squares slope of burst duration vs cells marked, over pure bookkeeping bursts. */
  motorMsPerCell: number | null;
  autoXsShare: number;

  exploration: ExplorationProfile;

  rules: RuleUsage[];
  sizes: SizeSpeed[];
  style: PlayStyle;
  highlights: Highlight[];
}

/**
 * Score the exploration ladder. `chanceRate` is the width-weighted mean of 1/w, so a player who
 * only ever picks in two-candidate units is compared against 50%, and one who picks in
 * seven-candidate units against 14% — which is the whole point: choosing right in a wide unit is
 * a different achievement from choosing right once the unit is down to a pair, and a single
 * hit rate that ignored the width would reward waiting rather than reading.
 */
function explorationOf(p: PlayerStats): ExplorationProfile {
  const widths = [...new Set([...Object.keys(p.aheadHits), ...Object.keys(p.aheadMisses)])]
    .map(Number).sort((a, b) => a - b);
  const byWidth: ExplorationProfile['byWidth'] = [];
  let ahead = 0, hits = 0, chance = 0, widthSum = 0, narrow = 0;
  for (const w of widths) {
    const h = p.aheadHits[w] ?? 0;
    const n = h + (p.aheadMisses[w] ?? 0);
    byWidth.push({ width: w, hits: h, total: n });
    ahead += n;
    hits += h;
    widthSum += w * n;
    chance += n / w;   // widths below 2 are not scored, so this never divides by zero
    if (w === 2) narrow += n;
  }
  const hitRate = ahead ? hits / ahead : null;
  const chanceRate = ahead ? chance / ahead : null;
  return {
    ahead,
    hits,
    hitRate,
    chanceRate,
    lift: hitRate !== null && chanceRate ? hitRate / chanceRate : null,
    meanWidth: ahead ? widthSum / ahead : null,
    narrowShare: ahead ? narrow / ahead : null,
    byWidth,
    recoveries: p.recoveryMarks.length,
    medianRecoveryMarks: median(p.recoveryMarks),
    clearShare: p.recoveryMarks.length ? p.recoveryByClear / p.recoveryMarks.length : null,
  };
}

function motorSlope(m: PlayerStats['motor']): number | null {
  if (m.n < 8) return null;
  const denom = m.n * m.sxx - m.sx * m.sx;
  if (Math.abs(denom) < 1e-9) return null;
  const slope = (m.n * m.sxy - m.sx * m.sy) / denom;
  return slope > 0 && slope < 5000 ? slope : null;
}

const sum = (o: Record<string | number, number>) => Object.values(o).reduce((a, b) => a + b, 0);

export function buildProfile(p: PlayerStats, pop: PlayerStats, record?: PlayerRecord): PlayerProfile {
  const explained = p.byClass.inference + p.byClass.lookahead;
  const popExplained = pop.byClass.inference + pop.byClass.lookahead;
  const inferenceFacts = explained + p.byClass.commit + p.byClass.unexplained + p.byClass['changed-mind'];

  const popRuleTotal = sum(pop.ruleCounts) || 1;
  const ruleTotal = sum(p.ruleCounts) || 1;
  const rules: RuleUsage[] = ALL_RULE_IDS
    .map(rule => {
      const medianMs = median(p.ruleGaps[rule] ?? []);
      const popMedianMs = median(pop.ruleGaps[rule] ?? []);
      return {
        rule,
        name: RULE_NAMES[rule],
        count: p.ruleCounts[rule] ?? 0,
        share: (p.ruleCounts[rule] ?? 0) / ruleTotal,
        popShare: (pop.ruleCounts[rule] ?? 0) / popRuleTotal,
        medianMs,
        popMedianMs,
        speedRatio: medianMs !== null && popMedianMs ? medianMs / popMedianMs : null,
        samples: (p.ruleGaps[rule] ?? []).length,
      };
    })
    .filter(r => r.count > 0 || r.popShare > 0)
    .sort((a, b) => b.count - a.count);

  const sizes: SizeSpeed[] = Object.keys(p.timesBySize)
    .map(Number)
    .sort((a, b) => a - b)
    .map(size => ({
      size,
      solves: p.timesBySize[size].length,
      medianMs: median(p.timesBySize[size])!,
      popMedianMs: median(pop.timesBySize[size] ?? []),
    }));

  const lookaheadTotal = sum(p.lookaheadDepth);
  const meanDepth = lookaheadTotal
    ? Object.entries(p.lookaheadDepth).reduce((a, [d, n]) => a + Number(d) * n, 0) / lookaheadTotal
    : 0;
  const ambigTotal = sum(p.ambiguity) || 1;

  const profile: PlayerProfile = {
    key: p.key,
    displayName: p.displayName,
    tokens: p.tokens.length,
    solves: p.solves,
    facts: p.facts,
    xMarks: p.xMarks,
    queenMarks: p.queenMarks,
    autoXMarks: p.autoXMarks,
    xPerQueen: p.queenMarks ? (p.xMarks + p.autoXMarks) / p.queenMarks : null,
    record: record ?? emptyRecord(),
    byClass: p.byClass,
    mistakeRate: p.facts ? p.byClass.mistake / p.facts : 0,
    falloutRate: p.facts ? p.byClass.fallout / p.facts : 0,
    bookkeepingShare: p.facts ? p.byClass.bookkeeping / p.facts : 0,
    unexplainedRate: inferenceFacts ? p.byClass.unexplained / inferenceFacts : 0,
    queens: p.queens,
    queenSplit: {
      forced: p.queensForced,
      lookahead: p.queensLookahead,
      commit: p.queensCommit,
      wrong: p.queensWrong,
      changedMind: Math.max(0, p.queens - p.queensForced - p.queensLookahead - p.queensCommit - p.queensWrong),
    },
    guessRate: p.queens ? p.queensCommit / p.queens : 0,
    lookaheadShare: explained ? p.byClass.lookahead / explained : 0,
    meanLookaheadDepth: meanDepth,
    lookaheadDepth: p.lookaheadDepth,
    commitWidths: p.commitWidths,
    uniqueAttributionRate: (p.ambiguity[1] ?? 0) / ambigTotal,
    undosPerSolve: p.solves ? p.undos / p.solves : 0,
    medianSolveMs: median(Object.values(p.timesBySize).flat()),
    medianSearchMs: median(p.searchGaps),
    motorMsPerCell: motorSlope(p.motor),
    autoXsShare: p.solves ? p.autoXsSolves / p.solves : 0,
    exploration: explorationOf(p),
    rules,
    sizes,
    style: { key: 'allrounder', label: '', text: '' },
    highlights: [],
  };

  profile.style = styleOf(profile, pop, popExplained);
  profile.highlights = highlightsFor(profile, pop, popExplained);
  return profile;
}

const pct = (x: number, digits = 1) => `${(100 * x).toFixed(digits)}%`;
const ratio = (a: number, b: number) => (b > 0 ? a / b : 1);
/** Strengths are ranked against each other, so one signal being 40× the average must not shut
 *  the other two out of the page. */
const cap = (x: number) => Math.min(3, x);

/** A highlight with the margin that earned it, so the best few can be picked and the rest cut. */
type Scored = Highlight & { score: number };

/**
 * Name what the player does, from the axis they deviate most on. Every branch is a compliment:
 * the point is to tell someone what their game *is* before telling them anything else about it,
 * and there is no ordering among them.
 */
function styleOf(prof: PlayerProfile, pop: PlayerStats, popExplained: number): PlayStyle {
  const popGuess = pop.queens ? pop.queensCommit / pop.queens : 0;
  const popLookahead = popExplained ? pop.byClass.lookahead / popExplained : 0;
  const popBookkeeping = pop.facts ? pop.byClass.bookkeeping / pop.facts : 0;
  const ex = prof.exploration;

  if (prof.facts < 200 || prof.queens < 40) {
    return {
      key: 'allrounder', label: 'Finding your style',
      text: 'A few more solves and this page can say what your game actually looks like.',
    };
  }

  const candidates: Array<PlayStyle & { score: number }> = [
    {
      key: 'explorer', label: 'Explorer', score: ratio(prof.guessRate, popGuess),
      text: `You put queens down to see what the board does with them — ${pct(prof.guessRate, 0)} of your placements are ahead of the proof, against ${pct(popGuess, 0)} for everyone else — and re-fit them as the picture fills in. Done well that is the fastest way through a board that refuses to open up.`,
    },
    {
      key: 'deducer', label: 'Deducer', score: ratio(popGuess, Math.max(prof.guessRate, 0.005)),
      text: `A queen goes down when the board has proved it and not before: ${pct(1 - prof.guessRate, 0)} of yours are justified at the moment you place them, against ${pct(1 - popGuess, 0)} across everyone. Nothing you build ever has to be taken back apart.`,
    },
    {
      key: 'visualizer', label: 'Visualizer', score: ratio(prof.lookaheadShare, popLookahead),
      text: `You carry the eliminations in your head instead of writing them down — ${pct(prof.lookaheadShare, 0)} of your deductions rest on marks you never made, chaining about ${prof.meanLookaheadDepth.toFixed(1)} rounds at a time.`,
    },
    {
      key: 'archivist', label: 'Bookkeeper', score: ratio(prof.bookkeepingShare, popBookkeeping),
      text: `You keep the board complete — ${pct(prof.bookkeepingShare, 0)} of your clicks are Xs the queens already imply, against ${pct(popBookkeeping, 0)} for everyone else. Everything you deduce, you deduce from a board that is telling the whole truth.`,
    },
  ];

  const best = candidates.reduce((a, b) => (b.score > a.score ? b : a));
  if (best.score < 1.2) {
    return {
      key: 'allrounder', label: 'All-rounder',
      text: `You deduce, you commit, and you write things down in about the proportions everyone else does — no one lever is carrying your game${ex.lift !== null && ex.lift > 1 ? ', and the queens you place ahead of the board land better than the odds' : ''}.`,
    };
  }
  const { score: _score, ...style } = best;
  return style;
}

/**
 * Turn the numbers into the few sentences a player would actually want to read.
 *
 * Two rules here, both deliberate. Strengths come first and there is always at least one: a page
 * that opens with what you are doing wrong is a page nobody opens twice, and every profile in the
 * corpus has something real to lead with. And there is at most one `focus` — chosen for payoff
 * rather than to complete a report card, and always a lever *inside* the player's own style. No
 * highlight ever tells someone to stop playing the way they play.
 *
 * Every claim is stated against the population, so "4% slips" is not good or bad in a vacuum.
 */
function highlightsFor(prof: PlayerProfile, pop: PlayerStats, popExplained: number): Highlight[] {
  const popMistake = pop.facts ? pop.byClass.mistake / pop.facts : 0;
  const popGuess = pop.queens ? pop.queensCommit / pop.queens : 0;
  const popLookahead = popExplained ? pop.byClass.lookahead / popExplained : 0;
  const popBookkeeping = pop.facts ? pop.byClass.bookkeeping / pop.facts : 0;
  const popEx = explorationOf(pop);
  const ex = prof.exploration;

  const strengths: Scored[] = [];
  const focus: Scored[] = [];
  // Lazy in `h`: most of these texts read numbers that are only non-null when `when` holds.
  const add = (list: Scored[], when: boolean, h: () => Scored) => { if (when) list.push(h()); };

  // ── Accuracy ──
  add(strengths, prof.facts >= 200 && prof.mistakeRate <= popMistake * 0.7, () => ({
    kind: 'strength', label: 'Accurate', score: cap(ratio(popMistake, Math.max(prof.mistakeRate, 5e-4))),
    text: `${pct(prof.mistakeRate)} of your marks are genuine slips, against ${pct(popMistake)} across everyone — and that count excludes anything that only went wrong because a speculative queen upstream did. When you mark a square, you have read it right.`,
  }));

  // ── The exploration ladder ──
  // Placing queens ahead of the proof is a method, and these are the four things that separate
  // doing it well from doing it hopefully.
  // Against the population lift, not against 1.0: everyone beats a coin flip here (the corpus
  // runs at 2×), so clearing chance is the price of entry, not a distinction.
  add(strengths,
    ex.ahead >= 15 && ex.lift !== null && popEx.lift !== null && ex.lift >= popEx.lift * 1.15,
    () => ({
      kind: 'strength', label: 'Reads the board', score: cap(ratio(ex.lift!, popEx.lift!) * 1.6),
      text: `Queens you place before the board has proved them fit ${pct(ex.hitRate!)} of the time, against the ${pct(ex.chanceRate!)} a coin flip among the same candidates would get — ${Math.round((ex.lift! - 1) * 100)}% ahead of the odds where everyone else manages ${Math.round((popEx.lift! - 1) * 100)}%. Choosing the square well is the whole skill in playing ahead, and you have it.`,
    }));

  add(strengths,
    ex.ahead >= 15 && ex.meanWidth !== null && popEx.meanWidth !== null
      && ex.meanWidth <= popEx.meanWidth * 0.85,
    () => ({
      kind: 'strength', label: 'Narrows first', score: cap(ratio(popEx.meanWidth!, ex.meanWidth!)),
      text: `When you place ahead of the board, the unit is already down to ${ex.meanWidth!.toFixed(1)} candidates — everyone else picks at ${popEx.meanWidth!.toFixed(1)}. ${pct(ex.narrowShare ?? 0, 0)} of yours are a straight two-way choice, which is the cheapest possible place to be wrong.`,
    }));

  add(strengths, ex.recoveries >= 6 && ex.clearShare !== null && ex.clearShare <= 0.12, () => ({
    kind: 'strength', label: 'Re-fits without restarting', score: 1.5 + (1 - ex.clearShare!),
    text: `${ex.recoveries} of your queens turned out not to fit, and ${pct(1 - ex.clearShare!, 0)} of them moved without the board being wiped. Relocating one queen while the rest of your work stands is the hardest part of playing this way, and you almost never lose the board.`,
  }));

  add(strengths,
    ex.recoveries >= 6 && ex.medianRecoveryMarks !== null && popEx.medianRecoveryMarks !== null
      && ex.medianRecoveryMarks <= popEx.medianRecoveryMarks * 0.75,
    () => ({
      kind: 'strength', label: 'Catches it early',
      score: cap(ratio(popEx.medianRecoveryMarks!, Math.max(ex.medianRecoveryMarks!, 0.5))),
      text: `A queen that does not fit gets ${ex.medianRecoveryMarks} further marks out of you before you spot it, against ${popEx.medianRecoveryMarks} for everyone else. You feel the board pushing back almost immediately.`,
    }));

  // ── Deduction, lookahead, bookkeeping ──
  add(strengths, prof.queens >= 40 && prof.guessRate <= 0.02, () => ({
    kind: 'strength', label: 'Never has to guess', score: 2.5,
    text: `Only ${pct(prof.guessRate)} of your queens go down without a deduction that reaches them — effectively all of them are proved before they are placed, against ${pct(popGuess)} for everyone else.`,
  }));

  add(strengths, prof.lookaheadShare >= popLookahead * 1.25 && prof.byClass.lookahead >= 20, () => ({
    kind: 'strength', label: 'Thinks ahead', score: cap(ratio(prof.lookaheadShare, popLookahead)),
    text: `${pct(prof.lookaheadShare)} of your deductions rest on eliminations you never wrote down — about ${prof.meanLookaheadDepth.toFixed(1)} rounds chained in your head, against ${pct(popLookahead)} for everyone else. You are paying in attention to save clicks.`,
  }));

  add(strengths,
    prof.facts >= 200 && prof.bookkeepingShare >= popBookkeeping * 1.15 && prof.mistakeRate <= popMistake,
    () => ({
      kind: 'strength', label: 'Keeps a complete board', score: cap(ratio(prof.bookkeepingShare, popBookkeeping)),
      text: `${pct(prof.bookkeepingShare, 0)} of your clicks are Xs the queens already imply, against ${pct(popBookkeeping, 0)} for everyone else — and your slip rate is below average with it. Every deduction you make comes off a board that is telling the whole truth.`,
    }));

  // ── Speed, where there is enough of it to mean anything ──
  const timed = prof.rules.filter(r => r.samples >= 8 && r.speedRatio !== null);
  const fastest = timed.length ? timed.reduce((a, b) => (b.speedRatio! < a.speedRatio! ? b : a)) : null;
  const slowest = timed.length >= 2 ? timed.reduce((a, b) => (b.speedRatio! > a.speedRatio! ? b : a)) : null;

  add(strengths, fastest !== null && fastest.speedRatio! <= 0.8, () => ({
    kind: 'strength', label: `Fast at ${fastest?.name.toLowerCase()}`, score: cap(ratio(1, fastest?.speedRatio ?? 1)),
    text: `You spot ${fastest?.name.toLowerCase()} in ${((fastest?.medianMs ?? 0) / 1000).toFixed(1)}s, ${Math.round((1 - (fastest?.speedRatio ?? 1)) * 100)}% quicker than average. Puzzles that lean on it are your puzzles.`,
  }));

  const sized = prof.sizes.filter(s => s.solves >= 3 && s.popMedianMs);
  const bestSize = sized.length
    ? sized.reduce((a, b) => (b.medianMs / b.popMedianMs! < a.medianMs / a.popMedianMs! ? b : a))
    : null;
  add(strengths, bestSize !== null && bestSize.medianMs <= bestSize.popMedianMs! * 0.85, () => ({
    kind: 'strength', label: `Quick on ${bestSize?.size}×${bestSize?.size}`,
    score: cap(ratio(bestSize?.popMedianMs ?? 1, bestSize?.medianMs ?? 1)),
    text: `Your median on ${bestSize?.size}×${bestSize?.size} is ${Math.round((1 - (bestSize?.medianMs ?? 0) / (bestSize?.popMedianMs ?? 1)) * 100)}% under everyone else's, over ${bestSize?.solves} solves.`,
  }));

  // ── The one thing worth working on ──
  add(focus, ex.recoveries >= 6 && ex.clearShare !== null && ex.clearShare >= 0.3, () => ({
    kind: 'focus', label: 'Undo back, do not clear', score: 3 + ex.clearShare!,
    text: `${pct(ex.clearShare!, 0)} of the time a queen that did not fit took the whole board with it. Nearly everything else you had marked was still true — undoing back to the queen keeps it, and this is the cheapest change on this page.`,
  }));

  add(focus,
    ex.ahead >= 15 && ex.meanWidth !== null && popEx.meanWidth !== null
      && ex.meanWidth >= popEx.meanWidth * 1.2,
    () => ({
      kind: 'focus', label: 'Commit one pass later', score: 2 + ratio(ex.meanWidth!, popEx.meanWidth!),
      text: `You pick at ${ex.meanWidth!.toFixed(1)} candidates on average against ${popEx.meanWidth!.toFixed(1)} for everyone else, and land ${pct(ex.hitRate!, 0)} of them. One more sweep of Xs before you choose usually halves the width, and the same reading of the board is worth far more on a narrower unit — it is the cheapest way to convert what you already do well into fewer retreats.`,
    }));

  add(focus,
    ex.recoveries >= 6 && ex.medianRecoveryMarks !== null && popEx.medianRecoveryMarks !== null
      && ex.medianRecoveryMarks >= popEx.medianRecoveryMarks * 1.3,
    () => ({
      kind: 'focus', label: 'Check the tightest region first',
      score: 1.5 + ratio(ex.medianRecoveryMarks!, Math.max(popEx.medianRecoveryMarks!, 0.5)),
      text: `You get ${ex.medianRecoveryMarks} marks deep on a queen before it has to move, against ${popEx.medianRecoveryMarks} for everyone else. Right after a speculative placement, look at the smallest region it touches: a wrong branch shows up there first, and catching it saves the marks in between.`,
    }));

  add(focus, prof.facts >= 200 && prof.mistakeRate >= popMistake * 1.4, () => ({
    kind: 'focus', label: 'The slips are fresh ones', score: 1 + ratio(prof.mistakeRate, popMistake),
    text: `${pct(prof.mistakeRate)} of your marks are slips against ${pct(popMistake)} across everyone, and that already excludes anything downstream of a speculative queen. These are single squares read wrong — usually mid-drag, so the drags are where the time is.`,
  }));

  add(focus, slowest !== null && slowest.speedRatio! >= 1.3 && slowest.rule !== fastest?.rule, () => ({
    kind: 'focus', label: `${slowest?.name} is the slow one`, score: 1 + (slowest?.speedRatio ?? 1) / 2,
    text: `${slowest?.name} takes you ${((slowest?.medianMs ?? 0) / 1000).toFixed(1)}s against ${((slowest?.popMedianMs ?? 0) / 1000).toFixed(1)}s for everyone else — the technique where practice buys the most seconds. The editor can generate puzzles that drill exactly it.`,
  }));

  const rank = (xs: Scored[]) => [...xs].sort((a, b) => b.score - a.score).map(({ score: _s, ...h }) => h);
  const top = rank(strengths).slice(0, 3);
  if (top.length) return [...top, ...rank(focus).slice(0, 1)];

  // Nothing cleared a bar. Say something true anyway — the fallback is never a criticism.
  if (prof.facts < 200) {
    return [{
      kind: 'note', label: 'Not enough data yet',
      text: 'Solve a few more puzzles and this fills in — the profile needs a couple of hundred marks before the rates settle.',
    }];
  }
  return [
    {
      kind: 'note', label: 'Solid all round',
      text: `Nothing about your game is far from the middle, which is its own result: ${pct(1 - prof.mistakeRate)} of your marks stand, and you finish. A signature strength usually shows up once you push one thing on purpose — narrower commits, or one more round carried in your head.`,
    },
    ...rank(focus).slice(0, 1),
  ];
}
